/**
 * Dashboard statistics — single source of truth for the six stat cards
 * shared between the Adviser and Assistant dashboards (CLAUDE.md §21).
 *
 * All calculations are pure functions over the in-memory `files` array
 * plus a `systemNotesByFile` map (loaded once per dashboard mount from
 * `getAllSystemNotes()` in fileNotesApi.js).
 */

import { computeBonusClock } from './bonusClock';
import { getMonthKeySAST } from './dateHelpers';

// Re-exported so existing consumers of `getMonthKeySAST` from this module
// keep working; the implementation now lives in dateHelpers.js (single
// home for all date derivation).
export { getMonthKeySAST } from './dateHelpers';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * True iff `timestamp` falls in the given YYYY-MM month (SAST).
 */
export function isInSelectedMonth(timestamp, monthYear) {
  if (!monthYear || !timestamp) return false;
  return getMonthKeySAST(timestamp) === monthYear;
}

/**
 * Compute the six stat-card values for `monthYear`.
 *
 * @returns {{
 *   submitted: number,
 *   completed: number,
 *   avgDays: string,        // "X.X" or "—"
 *   slaCompliance: number,  // integer percent; defaults to 100 when no completions
 *   bonusQualifying: number,
 * }}
 */
export function computeMonthStats(files, monthYear, systemNotesByFile = {}, opts = {}) {
  const now = typeof opts.now === 'number' ? opts.now : Date.now();
  // Test records (is_test = true, CLAUDE.md §33) are NEVER counted in any
  // file statistic — same contract as preparation requests (§10 / §21.1).
  const list = (Array.isArray(files) ? files : []).filter((f) => f.isTest !== true);

  const submittedInMonth = list.filter((f) => isInSelectedMonth(f.submittedAt, monthYear));
  const completedInMonth = list.filter(
    (f) => f.status === 'completed' && isInSelectedMonth(f.completedAt, monthYear),
  );

  // Avg. Days: calendar days from submission to completion, paused INCLUDED.
  let avgDays = '—';
  if (completedInMonth.length > 0) {
    const totalMs = completedInMonth.reduce((sum, f) => {
      if (!f.submittedAt || !f.completedAt) return sum;
      const ms = new Date(f.completedAt).getTime() - new Date(f.submittedAt).getTime();
      return sum + Math.max(0, ms);
    }, 0);
    avgDays = (totalMs / completedInMonth.length / DAY_MS).toFixed(1);
  }

  // Bonus Qualifying: completed-in-month files whose business-day bonus
  // clock is ≤ 3. Atwork-uploaded gate is enforced upstream by the
  // completion action, so every completed file already satisfies it.
  let bonusQualifying = 0;
  for (const f of completedInMonth) {
    const clock = computeBonusClock(f, systemNotesByFile[f.id] || [], { now });
    if (clock.qualifies) bonusQualifying += 1;
  }

  // SLA Compliance: same clock as Bonus Qualifying — % of completed-in-
  // month files that meet the ≤ 3 business-day target.
  const slaCompliance = completedInMonth.length > 0
    ? Math.round((bonusQualifying / completedInMonth.length) * 100)
    : 100;

  return {
    submitted: submittedInMonth.length,
    completed: completedInMonth.length,
    avgDays,
    slaCompliance,
    bonusQualifying,
  };
}

/**
 * Live count of currently-active files (in_progress or paused) whose
 * bonus clock has already exceeded 3 business days. Not month-bound —
 * overdue files remain overdue regardless of which month is being
 * viewed.
 */
export function computeOverdue(files, systemNotesByFile = {}, opts = {}) {
  const now = typeof opts.now === 'number' ? opts.now : Date.now();
  // Test records are excluded from the live Overdue count (CLAUDE.md §33).
  const list = (Array.isArray(files) ? files : []).filter((f) => f.isTest !== true);
  let count = 0;
  for (const f of list) {
    if (f.status !== 'in_progress' && f.status !== 'paused') continue;
    const clock = computeBonusClock(f, systemNotesByFile[f.id] || [], { now });
    if (!clock.qualifies && clock.firstDrawnAt !== null) count += 1;
  }
  return count;
}
